import { compact } from 'lodash';
import { useMemo } from 'react';
import { useRecoilValue } from 'recoil';

import { blockBenchmarkType, blockSettings } from 'venn-state';
import { useAppPrintMode } from '../../../print';
import {
  AVAILABILITY_KEY,
  BENCHMARK_KEY,
  FREQUENCY_KEY,
  getAvailableRangeValue,
  getBenchmarkNameRangeMap,
  getMaxFrequencyValue,
  useBlockId,
  type AnalysisRequest,
} from '../../../studio-blocks';
import { getRangeMapKeyFromRequest, useRequestsRangeMap } from '../../../studio-blocks/logic/useRequestsRangeMap';

export const useMetaRows = (requests: AnalysisRequest[]) => {
  const blockId = useBlockId();
  const blockSetting = useRecoilValue(blockSettings(blockId));
  const customBlockType = blockSetting.customBlockType;
  const benchmarkType = useRecoilValue(blockBenchmarkType(blockId));
  const rangeMap = useRequestsRangeMap(requests);
  const isPrinting = useAppPrintMode().inPrintModeOrReportLab;

  return useMemo(() => {
    const subjectIds = requests.map(getRangeMapKeyFromRequest);

    if (isPrinting || customBlockType === 'SCENARIO') {
      return [];
    }

    return compact([
      {
        key: AVAILABILITY_KEY,
        label: AVAILABILITY_KEY,
        value: getAvailableRangeValue(rangeMap, subjectIds),
        isMetadata: true,
      },
      {
        key: FREQUENCY_KEY,
        label: FREQUENCY_KEY,
        value: getMaxFrequencyValue(rangeMap, subjectIds),
        isMetadata: true,
      },
      benchmarkType === 'COMMON' || benchmarkType === 'INDIVIDUAL'
        ? {
            key: BENCHMARK_KEY,
            label: BENCHMARK_KEY,
            value: requests.map((request) => request.benchmark?.name),
            customBenchmarkType: benchmarkType,
            benchmarkNameRangeMap: getBenchmarkNameRangeMap(rangeMap, requests),
            isMetadata: true,
          }
        : undefined,
    ]);
  }, [requests, isPrinting, customBlockType, rangeMap, benchmarkType]);
};
